/**
 * useClusterManager — Manages knowledge area clusters on the canvas.
 *
 * Tracks cluster assignments, computes bounds from node positions,
 * handles apply/rename/dismiss operations, and provides the cluster
 * overlay data for rendering.
 */

import { useCallback, useState } from 'react';
import type { CanvasCluster } from '@shared/types.ts';
import type { CanvasFlowNode } from './useProcessCanvas';
import { mapToDisplayType } from '@shared/canvas-type-map.ts';

// ── Cluster color palette (8% opacity backgrounds, high contrast between adjacent) ──

const CLUSTER_COLORS = [
  'rgba(59, 130, 246, 0.08)',   // blue
  'rgba(16, 185, 129, 0.08)',   // emerald
  'rgba(245, 158, 11, 0.08)',   // amber
  'rgba(139, 92, 246, 0.08)',   // violet
  'rgba(236, 72, 153, 0.08)',   // pink
  'rgba(6, 182, 212, 0.08)',    // cyan
  'rgba(249, 115, 22, 0.08)',   // orange
  'rgba(168, 162, 158, 0.08)',  // stone
];

const CLUSTER_PADDING = 40; // padding around node bounding box

// ── Node dimensions (must match useProcessCanvas) ──

const NODE_DIMS: Record<string, { width: number; height: number }> = {
  STEP: { width: 300, height: 110 },
  ACTOR: { width: 160, height: 60 },
  TOOL: { width: 160, height: 50 },
  INSIGHT: { width: 240, height: 90 },
  TRIGGER: { width: 180, height: 60 },
};
const DEFAULT_DIMS = { width: 200, height: 80 };

// ── Types ──

export interface ClusterProposal {
  id: string;
  name: string;
  nodeIds: string[];
}

export interface UseClusterManagerReturn {
  clusters: CanvasCluster[];
  /** Pending proposal from the Interview Agent (null = no proposal active) */
  pendingProposal: ClusterProposal[] | null;
  /** Call when sectionComplete event arrives with proposed clusters */
  proposeCluster: (proposals: ClusterProposal[], nodes: CanvasFlowNode[]) => void;
  /** User accepted the proposal */
  applyProposal: (nodes: CanvasFlowNode[]) => void;
  /** User dismissed the proposal */
  dismissProposal: () => void;
  /** Rename a cluster */
  renameCluster: (clusterId: string, newName: string) => void;
  /** Recalculate bounds after node positions change */
  recalculateBounds: (nodes: CanvasFlowNode[]) => void;
}

function computeBounds(
  nodeIds: string[],
  nodes: CanvasFlowNode[],
): { x: number; y: number; width: number; height: number } {
  const clusterNodes = nodes.filter((n) => nodeIds.includes(n.id));
  if (clusterNodes.length === 0) return { x: 0, y: 0, width: 200, height: 100 };

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const node of clusterNodes) {
    const dims = NODE_DIMS[mapToDisplayType(node.data.canvasNodeType)] ?? DEFAULT_DIMS;
    const x = node.position.x;
    const y = node.position.y;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x + dims.width > maxX) maxX = x + dims.width;
    if (y + dims.height > maxY) maxY = y + dims.height;
  }

  return {
    x: minX - CLUSTER_PADDING,
    y: minY - CLUSTER_PADDING,
    width: maxX - minX + CLUSTER_PADDING * 2,
    height: maxY - minY + CLUSTER_PADDING * 2,
  };
}

export function useClusterManager(initialClusters?: CanvasCluster[]): UseClusterManagerReturn {
  const [clusters, setClusters] = useState<CanvasCluster[]>(initialClusters ?? []);
  const [pendingProposal, setPendingProposal] = useState<ClusterProposal[] | null>(null);

  const proposeCluster = useCallback(
    (proposals: ClusterProposal[], nodes: CanvasFlowNode[]) => {
      // Only show proposal if >= 3 unclustered nodes exist
      const clusteredIds = new Set(clusters.flatMap((c) => c.nodeIds));
      const unclusteredCount = nodes.filter((n) => !clusteredIds.has(n.id)).length;
      if (unclusteredCount < 3) return;

      setPendingProposal(proposals);
    },
    [clusters],
  );

  const applyProposal = useCallback(
    (nodes: CanvasFlowNode[]) => {
      if (!pendingProposal) return;

      const existingIds = new Set(clusters.map((c) => c.id));
      const newClusters: CanvasCluster[] = pendingProposal
        .filter((p) => !existingIds.has(p.id))
        .map((p, i) => ({
          id: p.id,
          name: p.name,
          nodeIds: p.nodeIds,
          color: CLUSTER_COLORS[(clusters.length + i) % CLUSTER_COLORS.length],
          bounds: computeBounds(p.nodeIds, nodes),
        }));

      setClusters((prev) => [...prev, ...newClusters]);
      setPendingProposal(null);
    },
    [pendingProposal, clusters],
  );

  const dismissProposal = useCallback(() => {
    setPendingProposal(null);
  }, []);

  const renameCluster = useCallback((clusterId: string, newName: string) => {
    setClusters((prev) =>
      prev.map((c) => (c.id === clusterId ? { ...c, name: newName } : c)),
    );
  }, []);

  const recalculateBounds = useCallback(
    (nodes: CanvasFlowNode[]) => {
      setClusters((prev) =>
        prev.map((c) => ({
          ...c,
          bounds: computeBounds(c.nodeIds, nodes),
        })),
      );
    },
    [],
  );

  return {
    clusters,
    pendingProposal,
    proposeCluster,
    applyProposal,
    dismissProposal,
    renameCluster,
    recalculateBounds,
  };
}
